
"use client";

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MissingCustomerForm } from "./MissingCustomerForm";
import { RootState } from "@store/store";
import { openMissingCustomerModal } from "@store/features/missingCustomerSlice";
import { userApi } from "@store/api-helpers/userApi";

/**
 * Interface for MissingCustomerModal component props
 */
interface MissingCustomerModalProps {
  /**
   * Optional flag to automatically check for missing customer data on initialization
   * @default true
   */
  autoCheck?: boolean;
}

/**
 * Component that checks if the authenticated user has associated customer data.
 * If no customer data is found, it automatically displays a modal with a form
 * to collect the missing information.
 *
 * @param props - Component properties
 * @returns A modal component with a form for collecting missing customer information
 */
export const MissingCustomerModal: React.FC<MissingCustomerModalProps> = ({ 
  autoCheck = true 
}) => {
  const dispatch = useDispatch();
  const { isAuthenticated, userId } = useSelector((state: RootState) => state.auth);
  
  const { data: userData, isSuccess: userDataFetched } = userApi.useGetUserDataQuery(userId || "", {
    skip: !isAuthenticated || !userId
  });
  

  
  const isSucceeded = useSelector((state: RootState) => 
    state.missingCustomer.status === "succeeded"
  );
  
  
  useEffect(() => {
    if (autoCheck && isAuthenticated && userDataFetched && !isSucceeded && userData) {
      
      if ('rawUsers' in userData && userData.rawUsers) {
        if (userData.rawUsers.kunden === null && userData.rawUsers.filialen === null) {
          dispatch(openMissingCustomerModal());
          return;
        }
      }
      
      const userDataArray = Array.isArray(userData) ? userData : [userData];
      
      const hasNoCustomerData = userDataArray.some(user => {
        if ('rawUsers' in user && user.rawUsers) {
          return user.rawUsers.kunden === null && user.rawUsers.filialen === null;
        }
        
        if ('users' in user && user.users) {
          return user.users.kunden === null && user.users.filialen === null;
        }
        
        return false;
      });
      
      if (hasNoCustomerData) {
        dispatch(openMissingCustomerModal());
      }
    }
  }, [autoCheck, dispatch, isAuthenticated, userData, userDataFetched, isSucceeded]);

  return <MissingCustomerForm />;
};
